/**
 * @module useAuth
 */
import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  useLoginMutation,
  useLogoutMutation,
  useVerifyMutation,
  useGetCurrentUserMutation,
  useUpdatePasswordMutation,
  useListUsersMutation,
  useGetUserByIdMutation,
  useUpdateUserStatusMutation,
  useUpdateUserRoleMutation,
} from '../services/auth.service'
import { selectIsAuthLoading, setIsAuthenticated, setIsAuthLoading } from '../state/auth.slice'
import { setAccessToken } from '../state/auth.slice'
import { setActiveUser } from '../state/user.slice'

export const useAuth = () => {
  const dispatch = useDispatch()
  const [executeLogin, { isLoading: isUsingLogin }] = useLoginMutation()
  const [executeLogout, { isLoading: isUsingLogout }] = useLogoutMutation()
  const [executeVerify, { isLoading: isUsingVerify }] = useVerifyMutation()
  const [executeGetCurrentUser, { isLoading: isGetCurrentUserLoading }] =
    useGetCurrentUserMutation()
  const [executeUpdatePassword, { isLoading: isUpdatePasswordLoading }] =
    useUpdatePasswordMutation()
  const [executeListUsers, { isLoading: isListUsersLoading }] = useListUsersMutation()
  const [executeGetUserById, { isLoading: isGetUserByIdLoading }] = useGetUserByIdMutation()
  const [executeUpdateUserStatus, { isLoading: isUpdateUserStatusLoading }] =
    useUpdateUserStatusMutation()
  const [executeUpdateUserRole, { isLoading: isUpdateUserRoleLoading }] =
    useUpdateUserRoleMutation()
  const isAuthLoading = useSelector(selectIsAuthLoading)

  const loginAsync = useCallback(
    async (email, password) => {
      if (isUsingLogin) {
        return { success: false }
      }
      dispatch(setIsAuthLoading(true))
      try {
        const response = await executeLogin({ email, password }).unwrap()
        console.log('loginAsync...', response)
        if (response.data) {
          dispatch(setAccessToken(response.data.token))
          dispatch(setIsAuthenticated(true))
          localStorage.setItem('access_token', response.data.token)
          return { success: true }
        }
        return { success: false }
      } catch (error) {
        console.error(error)
        return { success: false, error }
      } finally {
        dispatch(setIsAuthLoading(false))
      }
    },
    [dispatch, executeLogin, isUsingLogin],
  )

  const logoutAsync = useCallback(async () => {
    if (isUsingLogout) {
      return { success: false }
    }
    dispatch(setIsAuthLoading(true))
    try {
      await executeLogout().unwrap()
      dispatch(setIsAuthenticated(false))
      dispatch(setAccessToken(null))
      dispatch(setActiveUser(null))
      localStorage.removeItem('access_token')
      return { success: true }
    } catch (error) {
      console.error('#logoutAsync: Error during logout', error)
      // Even if the API call fails, we should still clear local state
      dispatch(setIsAuthenticated(false))
      dispatch(setAccessToken(null))
      dispatch(setActiveUser(null))
      localStorage.removeItem('access_token')
      return { success: true }
    } finally {
      dispatch(setIsAuthLoading(false))
    }
  }, [dispatch, executeLogout, isUsingLogout])

  const verifyAsync = useCallback(
    async (token) => {
      if (isUsingVerify) {
        return { success: false }
      }
      dispatch(setIsAuthLoading(true))
      try {
        const response = await executeVerify(token).unwrap()
        if (response.data) {
          dispatch(setActiveUser(response.data.user))
          dispatch(setIsAuthenticated(true))
          return { success: true }
        }
        return { success: false }
      } catch (error) {
        console.error(error)
        dispatch(setActiveUser(null))
        dispatch(setIsAuthenticated(false))
        dispatch(setAccessToken(null))
        localStorage.removeItem('access_token')
        return { success: false, error }
      } finally {
        dispatch(setIsAuthLoading(false))
      }
    },
    [dispatch, executeVerify, isUsingVerify],
  )

  const getCurrentUserAsync = useCallback(async () => {
    if (isGetCurrentUserLoading) {
      return
    }
    try {
      const response = await executeGetCurrentUser().unwrap()
      return response.data.user
    } catch (error) {
      console.error('#getCurrentUserAsync: Error fetching current user', error)
      throw error
    }
  }, [executeGetCurrentUser, isGetCurrentUserLoading])

  const updatePasswordAsync = useCallback(
    async (passwordData) => {
      if (isUpdatePasswordLoading) {
        return
      }
      try {
        const response = await executeUpdatePassword(passwordData).unwrap()
        return response.data.user
      } catch (error) {
        console.error('#updatePasswordAsync: Error updating password', error)
        throw error
      }
    },
    [executeUpdatePassword, isUpdatePasswordLoading],
  )

  const listUsersAsync = useCallback(
    async (params = {}) => {
      if (isListUsersLoading) {
        return
      }
      try {
        const response = await executeListUsers(params).unwrap()
        return response.data.users
      } catch (error) {
        console.error('#listUsersAsync: Error fetching users', error)
        throw error
      }
    },
    [executeListUsers, isListUsersLoading],
  )

  const getUserByIdAsync = useCallback(
    async (userId) => {
      if (isGetUserByIdLoading) {
        return
      }
      try {
        const response = await executeGetUserById(userId).unwrap()
        return response.data.user
      } catch (error) {
        console.error('#getUserByIdAsync: Error fetching user by ID', error)
        throw error
      }
    },
    [executeGetUserById, isGetUserByIdLoading],
  )

  const updateUserStatusAsync = useCallback(
    async (userId, status) => {
      if (isUpdateUserStatusLoading) {
        return
      }
      try {
        const response = await executeUpdateUserStatus({ userId, status }).unwrap()
        return response.data.user
      } catch (error) {
        console.error('#updateUserStatusAsync: Error updating user status', error)
        throw error
      }
    },
    [executeUpdateUserStatus, isUpdateUserStatusLoading],
  )

  const updateUserRoleAsync = useCallback(
    async (userId, role) => {
      if (isUpdateUserRoleLoading) {
        return
      }
      try {
        const response = await executeUpdateUserRole({ userId, role }).unwrap()
        return response.data.user
      } catch (error) {
        console.error('#updateUserRoleAsync: Error updating user role', error)
        throw error
      }
    },
    [executeUpdateUserRole, isUpdateUserRoleLoading],
  )

  const checkInitialAuth = useCallback(async () => {
    const accessToken = localStorage.getItem('access_token')
    if (accessToken) {
      return await verifyAsync(accessToken)
    } else {
      // No token to verify, but don't set loading to false here
      // Let the App.js handle setting loading to false after auth check
      return { success: false }
    }
  }, [verifyAsync])

  return {
    loginAsync,
    logoutAsync,
    verifyAsync,
    getCurrentUserAsync,
    updatePasswordAsync,
    listUsersAsync,
    getUserByIdAsync,
    updateUserStatusAsync,
    updateUserRoleAsync,
    checkInitialAuth,
    isAuthLoading,
    isUsingLogin,
    isUsingLogout,
    isUsingVerify,
    isGetCurrentUserLoading,
    isUpdatePasswordLoading,
    isListUsersLoading,
    isGetUserByIdLoading,
    isUpdateUserStatusLoading,
    isUpdateUserRoleLoading,
  }
}
